import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, from, switchMap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { KeycloakService } from '../services/keycloak.service';

// Guards against a burst of parallel 401s each triggering `keycloakService.login()`
// (only the first redirect is needed; subsequent calls are no-ops anyway).
let redirectingToLogin = false;

/** Test-only: resets the redirect-in-flight latch; call in `beforeEach`. */
export function __resetAuthInterceptorStateForTests(): void {
  redirectingToLogin = false;
}

/** Injects auth headers based on the request URL path. */
export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const keycloakService = inject(KeycloakService);

  // Admin routes → Keycloak Bearer token (refreshed before sending)
  if (req.url.startsWith(environment.apiBaseUrl + '/v1/admin')) {
    return from(keycloakService.ensureFreshToken()).pipe(
      switchMap(token => {
        const authed = req.clone({ setHeaders: { 'Authorization': `Bearer ${token}` } });
        return next(authed);
      }),
      catchError(err => {
        // 401 (including a rejected `ensureFreshToken`) → re-login; 403 is permission, not auth.
        const isAuthFailure = !(err instanceof HttpErrorResponse) || err.status === 401;
        if (isAuthFailure) {
          if (!redirectingToLogin) {
            redirectingToLogin = true;
            keycloakService.login();
          }
          return EMPTY;
        }
        return throwError(() => err);
      }),
    );
  }

  // Every other route is unauthenticated from the console's side: the admin
  // console never holds service API key material, so nothing else to attach.
  return next(req);
};
